import { FormActionsMui } from '@app/components/form/actions/FormActionsMui';
import { Page } from '@app/components/page/Page';
import { ComposeIcon } from '@app/modules/compose/components/ComposeIcon';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { compositionToSocialExportFragmentStub } from '@app/modules/compose/screens/upload/composition-to-social-export-fragment-stub';
import { ConnectionProfileDisplay } from '@app/modules/social-export/components/ConnectionProfileDisplay';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import {
  SocialExportMediaSelector,
  SocialExportNoSourcesAlert,
  SocialExportVideoPreview,
  socialExportPublishMessages,
} from '@app/modules/social-export/publish';
import { socialExport, webApi } from '@mi-front/api-clients';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, CircularProgress, Grid } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

import { useComposeSocialExportUploadPage } from './useComposeSocialExportUploadPage';

const publishLabelMessage = defineMessage({
  id: 'socialExport.form.publish',
  defaultMessage: 'Publicar',
});

export interface ComposeSocialExportUploadPageContentProps {
  composition: webApi.EnrichedCompositionDetailDto;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
}

/**
 * Layout for composition social upload once composition and edits are loaded; loads connection settings here.
 */
export function ComposeSocialExportUploadPageContent({
  composition,
  edits,
}: ComposeSocialExportUploadPageContentProps) {
  const { $t } = useIntl();
  const {
    platform,
    selectedSource,
    setSelectedSource,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm,
    handleUpload,
  } = useComposeSocialExportUploadPage({ composition, edits });
  const stationId = composition.stationId ?? '';

  const settingsQuery =
    socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>(
      stationId,
      platform.id,
      {
        query: {
          enabled: !!stationId,
        },
      },
    );

  const effectiveMediaUrl = renderedVideoUrl;
  const canPublish = effectiveMediaUrl !== null && hasSelectedRenderedEdit;

  const title = $t(socialExportPublishMessages.publishToPlatform, { platformName: platform.name });
  const fragmentStub = compositionToSocialExportFragmentStub(composition);

  if (!stationId) {
    return (
      <Page htmlTitle={title}>
        <Page.Header
          icon={platform.Icon()}
          title={title}
          breadcrumbs={
            <Page.Breadcrumbs
              items={[
                {
                  icon: <ComposeIcon fontSize="inherit" />,
                  content: composition.name,
                  href: composeRouteGenerator.view(composition.id),
                },
              ]}
            />
          }
        />
        <Page.Content maxWidth="lg">
          <Box sx={{ mt: 2 }}>{$t(socialExportPublishMessages.noStation)}</Box>
        </Page.Content>
      </Page>
    );
  }

  if (settingsQuery.isLoading || !settingsQuery.data) {
    return (
      <Page htmlTitle={title}>
        <Page.Header
          icon={platform.Icon()}
          title={title}
          breadcrumbs={
            <Page.Breadcrumbs
              items={[
                {
                  icon: <ComposeIcon fontSize="inherit" />,
                  content: composition.name,
                  href: composeRouteGenerator.view(composition.id),
                },
              ]}
            />
          }
        />
        <Page.Content maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        </Page.Content>
      </Page>
    );
  }

  return (
    <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
      <Page htmlTitle={title}>
        <Page.Header
          icon={platform.Icon()}
          title={title}
          breadcrumbs={
            <Page.Breadcrumbs
              items={[
                {
                  icon: <ComposeIcon fontSize="inherit" />,
                  content: composition.name,
                  href: composeRouteGenerator.view(composition.id),
                },
              ]}
            />
          }
        />
        <Page.Content maxWidth="lg">
          {!hasAvailableSources ? (
            <SocialExportNoSourcesAlert
              description={socialExportPublishMessages.panoramaNoSourcesComposition}
              linkTo={composeRouteGenerator.edits(composition.id)}
            />
          ) : (
            <>
              {showMediaSelector && (
                <Box sx={{ mb: 4, mt: 2 }}>
                  <SocialExportMediaSelector
                    includeFragmentOption={false}
                    value={selectedSource}
                    onChange={setSelectedSource}
                    edits={edits}
                  />
                </Box>
              )}
              {!canPublish && selectedSource
                ? $t(socialExportPublishMessages.mediaSelectorRenderNotReadyComposition)
                : effectiveMediaUrl && (
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={7}>
                        <Box mb={2}>
                          <ConnectionProfileDisplay stationId={stationId} platform={platform} />
                        </Box>
                        <PublishForm
                          fragment={fragmentStub}
                          videoUrl={effectiveMediaUrl}
                          onUpload={handleUpload}
                          defaultSettings={settingsQuery.data}
                          actions={
                            <FormActionsMui
                              separator
                              saveIcon={<UploadIcon />}
                              saveLabel={$t(publishLabelMessage)}
                              cancel
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <SocialExportVideoPreview videoUrl={effectiveMediaUrl} />
                      </Grid>
                    </Grid>
                  )}
            </>
          )}
        </Page.Content>
      </Page>
    </SocialExportContext.Provider>
  );
}
